// A per-viewer product order that is random across viewers but STABLE for one
// viewer — the nav's product rows and the article sidebar's featured rail both
// need "everyone sees a different few, but you see the same few all session".
//
// The order comes from a random per-viewer **seed** (localStorage, 24h) rather
// than a stored list of ids, and each item is ranked by `hash(seed + id)`. That
// choice is the whole point:
//
//   • Ranking is per-item, so REMOVING an item leaves the others in the same
//     relative order. The sidebar pool changes on every article (the current
//     story's own product is excluded), and a seeded Fisher–Yates over a
//     shorter array would deal a completely different hand each time.
//   • A stored list only remembers the items that WON. Anything outside it is
//     permanently "unseen", so a pool bigger than the slot count reshuffles the
//     losers on every page load — which is exactly how a sticky order stops
//     being sticky once an editor features more products than there are slots.
//   • A newly featured product gets a fair random rank instead of being pinned
//     to the front or stuck at the back.
//
// The seed rotates every 24h, so the picks a given viewer sees do rotate — just
// not mid-session. Reads and writes are client-only (localStorage): call this
// from an effect, never during render or SSR.

const SEED_TTL_MS = 24 * 60 * 60 * 1000
const SEED_KEY_PREFIX = 'shuffle-seed-'

// FNV-1a → a well-distributed number in [0, 1). Not cryptographic; it only has
// to scatter ids evenly and give the same answer every time for the same input.
// Exported for the popup A/B-group arm pick (match-path.ts), which needs the
// same random-across-viewers-but-stable-per-viewer property.
export const stableRank = (input: string): number => {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0) / 4294967296
}

// Survives remounts within the session, so a soft navigation between articles
// doesn't re-read storage (and can't reorder even if storage is unavailable).
const sessionSeeds = new Map<string, string>()

const readSeed = (storageKey: string): string | null => {
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) {
      return null
    }
    const { seed, timestamp } = JSON.parse(stored)
    if (typeof seed !== 'string' || typeof timestamp !== 'number' || Date.now() - timestamp > SEED_TTL_MS) {
      return null
    }
    return seed
  } catch {
    // Unparseable or localStorage unavailable (private mode, quota) — fall back
    // to a fresh seed. The order is then stable for the session but not the day.
    return null
  }
}

const writeSeed = (storageKey: string, seed: string): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ seed, timestamp: Date.now() }))
  } catch {
    // Same as above — the shuffle just won't stick past this session.
  }
}

/**
 * The viewer's shuffle seed for one surface, minted on first use and reused for
 * 24h. `name` scopes it, so two surfaces never share an order.
 */
export const viewerShuffleSeed = (name: string): string => {
  const cached = sessionSeeds.get(name)
  if (cached != null) {
    return cached
  }
  const storageKey = `${SEED_KEY_PREFIX}${name}`
  let seed = readSeed(storageKey)
  if (seed == null) {
    seed = Math.random().toString(36).slice(2)
    writeSeed(storageKey, seed)
  }
  sessionSeeds.set(name, seed)
  return seed
}

/**
 * Order `items` randomly-but-stably for this viewer. `idOf` must return a stable
 * identity (a release id or slug — NOT an array index, which would re-rank
 * everything whenever the pool changes length).
 */
export const stickyShuffle = <T>(items: T[], idOf: (item: T) => string, name: string): T[] => {
  if (items.length < 2) {
    return [...items]
  }
  const seed = viewerShuffleSeed(name)
  // Precompute so the comparator doesn't re-hash on every comparison, and keep
  // the id as a tiebreak so a hash collision still gives a total order.
  const ranked = items.map((item) => {
    const id = idOf(item)
    return { item, id, key: stableRank(seed + id) }
  })
  ranked.sort((a, b) => a.key - b.key || a.id.localeCompare(b.id))
  return ranked.map((entry) => entry.item)
}

/** Test seam: drops the in-session seed cache. Not used by app code. */
export const clearStickyShuffleCache = (): void => {
  sessionSeeds.clear()
}
